import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";

const initial = {
  name: "",
  email: "",
  phone: "",
  company: "",
  preferred_date: "",
  preferred_time: "",
  topic: "",
  message: "",
};

export default function BookingModal() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-booking", handler);
    return () => window.removeEventListener("open-booking", handler);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitLead({ ...form, type: "booking" });
      toast.success("Consultation request received — we'll confirm your slot shortly.");
      setForm(initial);
      setOpen(false);
    } catch {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-end md:items-center justify-center p-0 md:p-6 bg-black/70"
          onClick={() => setOpen(false)}
          data-testid="booking-modal"
        >
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="bg-white border-2 border-black shadow-[10px_10px_0_0_#CCFF00] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b-2 border-black bg-lime">
              <p className="font-display text-2xl md:text-3xl uppercase flex items-center gap-3">
                <CalendarCheck size={26} /> Book a Consultation
              </p>
              <button onClick={() => setOpen(false)} data-testid="booking-modal-close" aria-label="Close booking form" className="p-1 border-2 border-black bg-white hover:bg-black hover:text-lime transition-colors duration-200">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={onSubmit} className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="booking-form">
              <div>
                <label className="label-mono" htmlFor="bk-name">Name *</label>
                <input id="bk-name" data-testid="booking-name-input" required className="input-brutal" value={form.name} onChange={set("name")} placeholder="Jane Doe" />
              </div>
              <div>
                <label className="label-mono" htmlFor="bk-email">Email *</label>
                <input id="bk-email" data-testid="booking-email-input" required type="email" className="input-brutal" value={form.email} onChange={set("email")} placeholder="jane@company.com" />
              </div>
              <div>
                <label className="label-mono" htmlFor="bk-phone">Phone</label>
                <input id="bk-phone" data-testid="booking-phone-input" className="input-brutal" value={form.phone} onChange={set("phone")} placeholder="(808) 555-0000" />
              </div>
              <div>
                <label className="label-mono" htmlFor="bk-company">Company</label>
                <input id="bk-company" data-testid="booking-company-input" className="input-brutal" value={form.company} onChange={set("company")} placeholder="Company LLC" />
              </div>
              <div>
                <label className="label-mono" htmlFor="bk-date">Preferred date</label>
                <input id="bk-date" data-testid="booking-date-input" type="date" className="input-brutal" value={form.preferred_date} onChange={set("preferred_date")} />
              </div>
              <div>
                <label className="label-mono" htmlFor="bk-time">Preferred time (HST)</label>
                <select id="bk-time" data-testid="booking-time-select" className="input-brutal" value={form.preferred_time} onChange={set("preferred_time")}>
                  <option value="">Any time</option>
                  <option>Morning (9–12 HST)</option>
                  <option>Afternoon (12–3 HST)</option>
                  <option>Late afternoon (3–5 HST)</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label-mono" htmlFor="bk-topic">What should we focus on?</label>
                <select id="bk-topic" data-testid="booking-topic-select" className="input-brutal" value={form.topic} onChange={set("topic")}>
                  <option value="">General growth strategy</option>
                  <option>SEO</option>
                  <option>Social Media Marketing</option>
                  <option>Paid Advertising</option>
                  <option>Web Design & Development</option>
                  <option>Lead Generation</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label-mono" htmlFor="bk-msg">Anything else?</label>
                <textarea id="bk-msg" data-testid="booking-message-input" rows={3} className="input-brutal resize-none" value={form.message} onChange={set("message")} placeholder="Goals, timelines, current site..." />
              </div>
              <div className="md:col-span-2">
                <button type="submit" disabled={sending} data-testid="booking-submit-button" className="btn-brutal w-full disabled:opacity-60">
                  {sending ? "Sending..." : "Request My Consultation"}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
